import React, { useState, useEffect } from 'react';
import { AlertTriangle, Check, Sparkles, SkipForward } from 'lucide-react';

/**
 * Unified pipeline progress stepper.
 *
 * Props:
 * @param {Array} slots - Array of slot definitions:
 *   { id: string, label: string, skipped?: boolean }
 * @param {object|null} activePhase - Current phase info:
 *   { slotId: string, subLabel: string }
 *   null = not started yet
 * @param {boolean} isComplete - true when pipeline finished
 * @param {boolean} isError - true when pipeline failed
 * @param {string} [errorMessage] - error text
 * @param {object} slotTimings - Map of slotId → { startedAt: number, endedAt?: number }
 * @param {string} color - accent color hex
 * @param {object} theme - theme object (t)
 * @param {function} w - opacity helper
 */
const PipelineStepper = ({
  slots,
  activePhase,
  isComplete = false,
  isError = false,
  isCancelled = false,
  errorMessage,
  warningsCount = null,
  slotTimings = {},
  color = '#6366F1',
  theme = {},
  w = (o) => `rgba(255,255,255,${o})`,
}) => {
  const c = color;
  const t = theme;

  // ── Ticking elapsed time for the active slot ──────────────────────────
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (isComplete || isError || isCancelled) return;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [isComplete, isError, isCancelled]);

  // Determine slot states
  const activeSlotIndex = activePhase
    ? slots.findIndex(s => s.id === activePhase.slotId)
    : -1;

  const getSlotState = (slot, index) => {
    if (slot.skipped) return 'skipped';
    if (isComplete) return 'done';
    if (isCancelled && index === activeSlotIndex) return 'cancelled';
    if (isError && index === activeSlotIndex) return 'error';
    if (index < activeSlotIndex) return 'done';
    if (index === activeSlotIndex) return 'active';
    return 'pending';
  };

  const formatDuration = (ms) => {
    const sec = Math.round(ms / 1000);
    if (sec < 60) return `${sec}s`;
    const min = Math.floor(sec / 60);
    const remainder = sec % 60;
    return `${min}m ${remainder}s`;
  };

  const getSlotDuration = (slot, index) => {
    const timing = slotTimings[slot.id];
    if (!timing || !timing.startedAt) return null;
    if (timing.endedAt) {
      // Completed — show frozen duration
      return formatDuration(timing.endedAt - timing.startedAt);
    }
    if (getSlotState(slot, index) === 'active') {
      // Active — show ticking
      return formatDuration(now - timing.startedAt);
    }
    return null;
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: '20px', padding: '32px 0',
    }}>
      {/* Stepper dots + rails */}
      <div style={{
        display: 'flex', gap: '0', alignItems: 'center',
        flexWrap: 'wrap', justifyContent: 'center',
      }}>
        {slots.map((slot, i) => {
          const state = getSlotState(slot, i);
          const duration = getSlotDuration(slot, i);

          // Dot styles per state
          const dotStyle = {
            skipped: {
              background: 'transparent',
              border: `2px dashed ${w(0.15)}`,
              color: w(0.3),
            },
            done: {
              background: `${c}30`,
              border: `2px solid ${c}50`,
              color: c,
            },
            active: {
              background: `${c}15`,
              border: `2px solid ${c}`,
              color: c,
              boxShadow: `0 0 12px ${c}30`,
            },
            error: {
              background: 'rgba(239,68,68,0.15)',
              border: '2px solid rgba(239,68,68,0.6)',
              color: '#EF4444',
            },
            cancelled: {
              background: 'rgba(245,158,11,0.15)',
              border: '2px solid rgba(245,158,11,0.6)',
              color: '#F59E0B',
            },
            pending: {
              background: w(0.04),
              border: `2px solid ${w(0.08)}`,
              color: t.textDim || '#484858',
            },
          }[state];

          // Rail between dots
          const rail = i > 0 ? (
            <div style={{
              width: '32px', height: '1px',
              background: (state === 'done' || state === 'skipped') ? `${c}40` : w(0.08),
              transition: 'background 0.3s',
            }} />
          ) : null;

          const labelColor = {
            skipped: w(0.3),
            done: t.textMuted || '#8a8a9e',
            active: c,
            error: '#EF4444',
            cancelled: '#F59E0B',
            pending: t.textDim || '#484858',
          }[state];

          return (
            <React.Fragment key={slot.id}>
              {rail}
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                gap: '6px', minWidth: '80px',
              }}>
                {/* Duration above dot */}
                <span style={{
                  fontSize: '10px',
                  fontFamily: "'JetBrains Mono', monospace",
                  color: state === 'active' ? c : (t.textMuted || '#7a7a8e'),
                  opacity: duration ? 1 : 0,
                  height: '14px',
                  transition: 'opacity 0.3s',
                }}>
                  {duration || ''}
                </span>

                {/* Dot */}
                <div style={{
                  width: '28px', height: '28px', borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '11px', fontWeight: 600,
                  transition: 'all 0.3s',
                  ...dotStyle,
                }}>
                  {state === 'done' ? <Check size={12} /> :
                   state === 'skipped' ? <SkipForward size={10} /> :
                   i + 1}
                </div>

                {/* Label */}
                <span style={{
                  fontSize: '11px',
                  fontFamily: "'JetBrains Mono', monospace",
                  color: labelColor,
                  textAlign: 'center',
                  transition: 'color 0.3s',
                }}>
                  {state === 'skipped' ? 'Saltato' : slot.label}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Sub-label for active phase */}
      {activePhase && !isComplete && !isError && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px',
        }}>
          <Sparkles size={14} color={c} />
          <span style={{ color: t.textMuted || '#7a7a8e' }}>
            {activePhase.subLabel}
          </span>
        </div>
      )}

      {/* Completion message */}
      {isComplete && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px',
          color: warningsCount != null && warningsCount > 0 ? '#F59E0B' : '#10B981',
        }}>
          {warningsCount != null && warningsCount > 0 ? (
            <>
              <AlertTriangle size={14} />
              <span>
                Completato con {warningsCount} {warningsCount === 1 ? 'avviso' : 'avvisi'}
              </span>
            </>
          ) : (
            <>
              <Check size={14} />
              <span>Completato!</span>
            </>
          )}
        </div>
      )}

      {/* Error message */}
      {isError && errorMessage && (
        <div style={{
          marginTop: '4px', padding: '10px 14px', borderRadius: '8px',
          background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
          color: '#EF4444', fontSize: '13px', maxWidth: '500px', textAlign: 'center',
        }}>
          {errorMessage}
        </div>
      )}

      {/* Cancelled message */}
      {isCancelled && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px',
          color: '#F59E0B',
        }}>
          <AlertTriangle size={14} />
          <span>Annullato dall'utente</span>
        </div>
      )}
    </div>
  );
};

export default PipelineStepper;
